"use client";

// The Halloween effect: three small spiders wander across the screen (one
// requestAnimationFrame loop, transform only). A click squashes one and
// leaves a cartoon splat that fades. Mounted by HogtidLager, which decides
// when (date, account setting, page, device).

import { useEffect, useRef, useState } from "react";
import styles from "./hogtid.module.css";

const ANTAL = 3;
const STORLEK = 30;

interface Spindel {
  x: number;
  y: number;
  riktning: number; // radians, 0 = right
  fart: number; // px per second
  vridning: number; // current turn rate, radians per second
  vilaTill: number; // timestamp: standing still until then
  borta: boolean;
}

function nySpindel(b: number, h: number): Spindel {
  return {
    x: STORLEK + Math.random() * Math.max(1, b - 2 * STORLEK),
    y: STORLEK + Math.random() * Math.max(1, h - 2 * STORLEK),
    riktning: Math.random() * Math.PI * 2,
    fart: 28 + Math.random() * 22,
    vridning: 0,
    vilaTill: 0,
    borta: false,
  };
}

export default function Spindlar() {
  const [klattar, setKlattar] = useState<{ id: number; x: number; y: number; vrid: number }[]>([]);
  const elRef = useRef<(HTMLButtonElement | null)[]>([]);
  const spindlar = useRef<Spindel[]>([]);


  useEffect(() => {
    const b = () => window.innerWidth;
    const h = () => window.innerHeight;
    spindlar.current = Array.from({ length: ANTAL }, () => nySpindel(b(), h()));
    let forra = performance.now();
    let id = 0;

    const steg = (nu: number) => {
      // A long gap (tab was hidden) must not teleport the spiders.
      // ~30 updates a second is smooth enough at walking pace and halves the work.
      if (nu - forra < 30) {
        id = requestAnimationFrame(steg);
        return;
      }
      const dt = Math.min(0.08, (nu - forra) / 1000);
      forra = nu;
      const bredd = b();
      const hojd = h();
      spindlar.current.forEach((s, i) => {
        const el = elRef.current[i];
        if (!el || s.borta) return;
        if (nu < s.vilaTill) {
          el.dataset.vilar = "1";
        } else {
          el.dataset.vilar = "0";
          // A gently wandering heading, now and then a pause.
          s.vridning += (Math.random() - 0.5) * 6 * dt;
          s.vridning = Math.max(-1.6, Math.min(1.6, s.vridning));
          s.riktning += s.vridning * dt;
          // Turn back from the edges.
          const marg = STORLEK * 1.5;
          const motX = s.x < marg ? 1 : s.x > bredd - marg ? -1 : 0;
          const motY = s.y < marg ? 1 : s.y > hojd - marg ? -1 : 0;
          if (motX || motY) {
            const mal = Math.atan2(motY || Math.sin(s.riktning), motX || Math.cos(s.riktning));
            const diff = Math.atan2(Math.sin(mal - s.riktning), Math.cos(mal - s.riktning));
            s.riktning += diff * Math.min(1, 4 * dt);
          }
          s.x += Math.cos(s.riktning) * s.fart * dt;
          s.y += Math.sin(s.riktning) * s.fart * dt;
          s.x = Math.max(0, Math.min(bredd - STORLEK, s.x));
          s.y = Math.max(0, Math.min(hojd - STORLEK, s.y));
          if (Math.random() < 0.15 * dt) s.vilaTill = nu + 600 + Math.random() * 2200;
        }
        // The drawing faces up, so heading 0 (right) is +90°.
        el.style.transform = `translate(${s.x}px, ${s.y}px) rotate(${s.riktning + Math.PI / 2}rad)`;
      });
      id = requestAnimationFrame(steg);
    };
    id = requestAnimationFrame(steg);
    return () => cancelAnimationFrame(id);
  }, []);

  const klamma = (i: number) => {
    const s = spindlar.current[i];
    const el = elRef.current[i];
    if (!s || !el || s.borta) return;
    s.borta = true;
    el.dataset.klammd = "1";
    // A cartoon splat where it was, fading out on its own.
    setKlattar((k) => [...k, { id: performance.now(), x: s.x + STORLEK / 2, y: s.y + STORLEK / 2, vrid: Math.random() * 360 }]);
  };

  return (
    <>
      {klattar.map((k) => (
        <svg
          key={k.id}
          className={styles.klatt}
          viewBox="-30 -30 60 60"
          width={64}
          height={64}
          style={{ left: k.x - 32, top: k.y - 32, rotate: `${k.vrid}deg` }}
          onAnimationEnd={() => setKlattar((alla) => alla.filter((x) => x.id !== k.id))}
        >
          <path
            d="M0 -13 C5 -14 7 -9 11 -10 C15 -11 16 -5 13 -2 C17 1 15 7 10 7 C10 12 4 14 1 11 C-3 15 -9 12 -8 7 C-13 8 -16 2 -12 -1 C-16 -5 -12 -11 -7 -9 C-6 -13 -3 -13 0 -13 Z"
            fill="var(--color-hogtid-blod)"
          />
          <circle cx="19" cy="-12" r="2.6" fill="var(--color-hogtid-blod)" />
          <circle cx="-19" cy="10" r="3" fill="var(--color-hogtid-blod)" />
          <circle cx="7" cy="20" r="2" fill="var(--color-hogtid-blod)" />
          <circle cx="-14" cy="-17" r="1.7" fill="var(--color-hogtid-blod)" />
          <circle cx="22" cy="6" r="1.4" fill="var(--color-hogtid-blod)" />
          <circle cx="-23" cy="-3" r="1.5" fill="var(--color-hogtid-blod)" />
        </svg>
      ))}
      {Array.from({ length: ANTAL }, (_, i) => (
        <button
          key={i}
          ref={(el) => {
            elRef.current[i] = el;
          }}
          type="button"
          tabIndex={-1}
          className={styles.spindel}
          title="Smäll bort spindeln"
          onClick={(e) => {
            e.stopPropagation();
            klamma(i);
          }}
          onAnimationEnd={(e) => {
            if (e.animationName.includes("klams")) e.currentTarget.style.display = "none";
          }}
        >
          <svg viewBox="0 0 40 40" width={STORLEK} height={STORLEK}>
            <g className={styles.benV} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none">
              <path d="M17 17 L9 9 L5 4" />
              <path d="M16 20 L7 16 L2 14" />
              <path d="M16 23 L7 25 L2 28" />
              <path d="M17 26 L10 32 L6 37" />
            </g>
            <g className={styles.benH} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none">
              <path d="M23 17 L31 9 L35 4" />
              <path d="M24 20 L33 16 L38 14" />
              <path d="M24 23 L33 25 L38 28" />
              <path d="M23 26 L30 32 L34 37" />
            </g>
            <ellipse cx="20" cy="26" rx="5.5" ry="7" fill="currentColor" />
            <circle cx="20" cy="16" r="4" fill="currentColor" />
          </svg>
        </button>
      ))}
    </>
  );
}
